export function formatTimeAgo(date: Date | string, language: string): string {
  try {
    const now = new Date();
    const then = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    
    // Manual translations for Yiddish relative times
    // This ensures we have translations even if Intl.RelativeTimeFormat doesn't work properly on mobile
    if (language === 'yi') {
      if (diffInSeconds < 60) return `${Math.floor(diffInSeconds)} סעקונדעס צוריק`;
      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return minutes === 1 ? 'איין מינוט צוריק' : `${minutes} מינוט צוריק`;
      }
      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return hours === 1 ? 'איין שעה צוריק' : `${hours} שעה צוריק`;
      }
      if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return days === 1 ? 'איין טאָג צוריק' : `${days} טעג צוריק`;
      }
      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return months === 1 ? 'איין חודש צוריק' : `${months} חדשים צוריק`;
      }
      const years = Math.floor(diffInSeconds / 31536000);
      return years === 1 ? 'איין יאָר צוריק' : `${years} יאָר צוריק`;
    }
    
    // Manual translations for Hebrew relative times
    if (language === 'he') {
      if (diffInSeconds < 60) return `לפני ${Math.floor(diffInSeconds)} שניות`;
      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return minutes === 1 ? 'לפני דקה' : `לפני ${minutes} דקות`;
      }
      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return hours === 1 ? 'לפני שעה' : `לפני ${hours} שעות`;
      }
      if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return days === 1 ? 'לפני יום' : `לפני ${days} ימים`;
      }
      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return months === 1 ? 'לפני חודש' : `לפני ${months} חודשים`;
      }
      const years = Math.floor(diffInSeconds / 31536000);
      return years === 1 ? 'לפני שנה' : `לפני ${years} שנים`;
    }
    
    // For other languages or as a fallback, try to use Intl.RelativeTimeFormat
    try {
      // Check if Intl.RelativeTimeFormat is supported
      if (typeof Intl === 'undefined' || !Intl.RelativeTimeFormat) {
        throw new Error('Intl.RelativeTimeFormat not supported');
      }
      
      const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
      
      if (diffInSeconds < 60) return rtf.format(-Math.floor(diffInSeconds), 'second');
      if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
      if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
      if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
      if (diffInSeconds < 31536000) return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
      return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
    } catch (e) {
      // If there's an error with the specific language, try with just the language code
      try {
        const languageCode = language.split('-')[0];
        const rtf = new Intl.RelativeTimeFormat(languageCode, { numeric: 'auto' });
        
        if (diffInSeconds < 60) return rtf.format(-Math.floor(diffInSeconds), 'second');
        if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
        if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
        if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
        if (diffInSeconds < 31536000) return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
        return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
      } catch (innerError) {
        // English fallback if everything else fails
        if (diffInSeconds < 60) return `${Math.floor(diffInSeconds)} seconds ago`;
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
        return `${Math.floor(diffInSeconds / 31536000)} years ago`;
      }
    }
  } catch (error) {
    // Final fallback if everything else fails
    console.error('Error formatting relative time:', error);
    return new Date(date).toLocaleDateString(language);
  }
} 